import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import "./ForgotPasswordPage.scss";
import { requestPasswordResetEmail } from "@/api/auth";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { ReactComponent as TieOutLogo } from "@/assets/icons/tie-out.svg";
import { ReactComponent as RecoverPasswordIcon } from "@/assets/icons/recover-password.svg";
import { ReactComponent as ForgotPasswordMail } from "@/assets/icons/forgot-password-mail.svg";

type FormValues = {
  email: string;
};

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    setErrorMessage(null);
    requestPasswordResetEmail(data.email)
      .then(() => {
        setIsSubmitted(true);
      })
      .catch(() => {
        setErrorMessage(t("errors.generic"));
      });
  };

  return (
    <div className="forgot-password-page">
      <TieOutLogo className="forgot-password-logo" />

      <div className="forgot-password-card">
        <div className="recover-password-icon-wrap">
          <RecoverPasswordIcon className="recover-password-icon" aria-hidden />
        </div>

        <h1 className="forgot-password-title">
          {t("pages.auth.forgotPassword.title")}
        </h1>

        {isSubmitted ? (
          <>
            <p className="forgot-password-success">
              {t("pages.auth.forgotPassword.successMessage")}
            </p>
            <button
              type="button"
              className="forgot-password-back"
              onClick={() => navigate("/login")}
            >
              {t("pages.auth.forgotPassword.backToLogin")}
            </button>
          </>
        ) : (
          <>
            <p className="forgot-password-subtitle">
              {t("pages.auth.forgotPassword.subtitle")}
            </p>

            <form
              className="forgot-password-form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div className="forgot-password-field">
                <label
                  className="forgot-password-label"
                  htmlFor="forgot-password-email"
                >
                  {t("pages.auth.forgotPassword.fields.email")}
                </label>
                <div className="forgot-password-input-wrap">
                  <input
                    id="forgot-password-email"
                    className="forgot-password-input"
                    type="email"
                    autoComplete="email"
                    placeholder={t(
                      "pages.auth.forgotPassword.fields.emailPlaceholder",
                    )}
                    {...register("email", {
                      required: true,
                      pattern:
                        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    })}
                  />
                  <ForgotPasswordMail
                    className="forgot-password-mail-icon"
                    aria-hidden
                  />
                </div>
                {errors.email && (
                  <span className="forgot-password-error">
                    {errors.email.type === "required"
                      ? t("pages.auth.forgotPassword.errors.emailRequired")
                      : t("pages.auth.forgotPassword.errors.emailInvalid")}
                  </span>
                )}
              </div>

              {errorMessage && (
                <p className="forgot-password-error">{errorMessage}</p>
              )}

              <div className="forgot-password-submit">
                <GenericButton
                  label={t("pages.auth.forgotPassword.actions.sendLink")}
                  theme="primary"
                />
              </div>
            </form>

            <Link className="forgot-password-back" to="/login">
              {t("pages.auth.forgotPassword.backToLogin")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
